import { useEffect, useState } from 'react';

import { BackgroundProps, BackgroundVariant, FitViewOptions, useStore, Viewport } from 'reactflow';

import useAuth from '@hooks/useAuth';

import LocalStorageService, { StorageKey } from '../util/LocalStorageService';

export const DEFAULT_CANVAS_BG_COLOR = '#f9f9f9';
export const DEFAULT_CANVAS_GRID_COLOR = '#eee';
export const DEFAULT_ZOOM_LEVEL = 0.85; // 85%

export enum ExtendedBackgroundVariant {
    Cross = BackgroundVariant.Cross,
    Lines = BackgroundVariant.Lines,
    Dots = BackgroundVariant.Dots,
    Solid = 'solid',
}
export type CanvasBackgroundProps = BackgroundProps & {
    backgroundColor?: string;
};
export type CanvasBackgroundWithVariantProps = Omit<CanvasBackgroundProps, 'variant'> & {
    variant: ExtendedBackgroundVariant;
};

const fitViewOptions: FitViewOptions = {
    padding: 0.2,
    includeHiddenNodes: false,
    minZoom: 0.1,
    maxZoom: 4,
    duration: 1000,
};

const defaultOptions: CanvasBackgroundProps = {
    backgroundColor: DEFAULT_CANVAS_BG_COLOR,
    color: DEFAULT_CANVAS_GRID_COLOR,
    gap: 12,
    lineWidth: 1,
    size: 1,
    variant: BackgroundVariant.Lines,
};

const useCanvasSettings = ({ experiment_id, canvasLoaded }: { experiment_id?: string; canvasLoaded?: boolean }) => {
    const { authReady, isLoggedIn } = useAuth();
    const [backgroundOptions, setBackgroundOptions] = useState<CanvasBackgroundProps>(defaultOptions);
    const [showMiniMap, setShowMiniMap] = useState<boolean>(true);
    const [exportMode, setExportMode] = useState<boolean>(false);
    const [viewport, setViewport] = useState<Viewport>();
    // const [viewportError, setViewportError] = useState<string>('');
    const [panOnScroll, setPanOnScroll] = useState<boolean>(true);

    // Use the storage key from the StorageKey enum
    const STORAGE_KEY = StorageKey.CANVAS_SETTINGS_EXPERIMENTS;

    const getExperimentKey = () => {
        // Use experiment_id as the key, or 'global' if no experiment_id
        return experiment_id || 'global';
    };

    const restoreLocalStorageSettings = () => {
        const experimentKey = getExperimentKey();

        // Use LocalStorageService to safely get the settings
        const allParsedSettings = LocalStorageService.getObject<Record<string, any>>(STORAGE_KEY, {}) || {};

        // Get settings for this specific experiment
        const experimentSettings = allParsedSettings[experimentKey] || {};

        if ('backgroundOptions' in experimentSettings) {
            setBackgroundOptions(experimentSettings.backgroundOptions);
        }
        if ('showMiniMap' in experimentSettings) {
            setShowMiniMap(experimentSettings.showMiniMap);
        }
        if ('exportMode' in experimentSettings) {
            setExportMode(experimentSettings.exportMode);
        }
        if ('viewport' in experimentSettings) {
            setViewport(experimentSettings.viewport);
        }
        if ('panOnScroll' in experimentSettings) {
            setPanOnScroll(experimentSettings.panOnScroll);
        }
    };

    const updateLocalStorageSettings = (newSettings: Record<string, any>) => {
        const experimentKey = getExperimentKey();

        // Use LocalStorageService to safely get the settings
        const allParsedSettings = LocalStorageService.getObject<Record<string, any>>(STORAGE_KEY, {}) || {};

        // Get or initialize settings for this experiment
        const experimentSettings = allParsedSettings[experimentKey] || {};

        // Update experiment settings
        const updatedExperimentSettings = { ...experimentSettings, ...newSettings };

        // Update all settings
        const updatedAllSettings = {
            ...allParsedSettings,
            [experimentKey]: updatedExperimentSettings,
        };

        // Use LocalStorageService to safely save the settings
        LocalStorageService.setObject(STORAGE_KEY, updatedAllSettings);
    };

    useEffect(() => {
        if (!experiment_id || !authReady || !isLoggedIn || !canvasLoaded) return;

        restoreLocalStorageSettings();
    }, [experiment_id, canvasLoaded]);

    // We no longer save viewport to the server, only to localStorage
    // Viewport changes are user-specific and not meant to be shared across users

    const zoomLevel = useStore((store) => store.transform[2]);

    const updateBackgroundOptions = (newOptions: BackgroundProps) => {
        setBackgroundOptions(newOptions);
        updateLocalStorageSettings({ backgroundOptions: newOptions });
    };

    const handleUpdateViewport = (newPort: Viewport) => {
        setViewport(newPort);
        updateLocalStorageSettings({ viewport: newPort });
    };

    const handlePanOnScroll = (shouldPanOnScroll: boolean) => {
        setPanOnScroll(shouldPanOnScroll);
        updateLocalStorageSettings({ panOnScroll: shouldPanOnScroll });
    };

    const toggleMiniMap = () => {
        setShowMiniMap(!showMiniMap);
        updateLocalStorageSettings({ showMiniMap: !showMiniMap });
    };

    const toggleExportMode = () => {
        setExportMode(!exportMode);
        updateLocalStorageSettings({ exportMode: !exportMode });
    };

    // Keeping this function for reference, but it's no longer used
    // We're now only saving viewport to localStorage
    // const saveViewport = () => {
    //     if (!experiment_id || !!focusedInputRef.current?.id) return;
    //     try {
    //         api.put(Endpoints.lab.experiment.canvasFlow(experiment_id), { viewport });
    //     } catch {
    //         logger.error(new Error('Failed to save new canvas viewport'));
    //         setViewportError('Failed to save canvas viewport!');
    //     }
    // };

    return {
        backgroundOptions,
        exportMode,
        fitViewOptions,
        handlePanOnScroll,
        handleUpdateViewport,
        panOnScroll,
        setExportMode,
        showMiniMap,
        toggleExportMode,
        toggleMiniMap,
        updateBackgroundOptions,
        viewport,
        // viewportError,
        zoomLevel,
    };
};

export default useCanvasSettings;
